import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit } from "@angular/core";
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { isEmpty } from "lodash";
import moment from "moment";
import { CustomValidationService } from "src/app/cbta-web/services/custom-validation.service";
import { CONSTANT } from "src/app/constant";
import { FormDataOptions, StaffDetails } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ProfileService } from "src/app/shared/services/profile.service";
import { SharedService } from "src/app/shared/services/shared.service";
import {
	applyOwnStaffNumberFailure,
	clearOwnStaffNumberFailure,
	clearStaffLookupFailureOnValueChange,
	isInstructorOwnStaffNumber,
	patchStaffLicenseExpiryDate,
} from "src/app/shared/services/staff-lookup.helper";
import { clearCandidateStaffDerivedFields } from "../../shared-v2/utils/draft-staff-validation.utils";
import { ContainerRatingsService } from "../../services/base/container-ratings.services";

@Component({
	selector: "app-candidate-details",
	templateUrl: "./candidate-details.component.html",
	styleUrls: ["./candidate-details.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CandidateDetailsComponent implements OnInit {
	sessions!: FormDataOptions[];
	candidateForm!: UntypedFormGroup;
	parentForm!: UntypedFormArray;
	parentFormArrayIndex: number = 0;
	panelOpenState: boolean = false;
	currentDate: Date = new Date();
	formType!: string | undefined;

	@Input("candidateForm") set _(value: any) {
		this.candidateForm = value as UntypedFormGroup;
		// Ensure formType is set from service
		if (this.containerRatingService) {
			this.formType = this.containerRatingService.formType;
		}
	}

	@Input("sessions")
	set sessionsInput(value: any) {
		this.sessions = value as FormDataOptions[];
	}

	DATE_FORMAT = CONSTANT.DATE_FORMAT;

	today: Date = new Date();
	isOnline: boolean = true;

	datePickerRef?: ElementRef;
	datePickerInput = new UntypedFormControl(new Date());

	constructor(
		public sharedService: SharedService,
		private httpService: HttpService,
		private loaderService: LoaderService,
		private cvs: CustomValidationService,
		private cd: ChangeDetectorRef,
		public containerRatingService: ContainerRatingsService,
		private profileService: ProfileService,
	) {}

	ngOnInit(): void {
		// Ensure formType is set from service
		if (!this.formType) {
			this.formType = this.containerRatingService.formType;
		}

		this.sharedService.getIsStatusOnline().subscribe((status: boolean) => {
			console.log("CandidateDetailsComponent ngOnInit : ", status);
			this.isOnline = status;
		});
		this.parentForm = this.candidateForm.parent as UntypedFormArray;
		this.parentFormArrayIndex = this.parentForm.controls.findIndex((subform) => subform == this.candidateForm);
		this.staffNumber.valueChanges.subscribe(() => {
			clearStaffLookupFailureOnValueChange(this.staffNumber);
		});

		this.licenseExpiryDate.valueChanges.subscribe((a) => {
			if (a) {
				if (a !== this.licenseExpiryDate.value) {
					this.licenseExpiryDate?.patchValue(a);
				}
				const date = moment(a).toDate();
				this.datePickerInput.patchValue(date);
				this.updateDisplayDateFormat(date);
			}
		});
	}

	updateDisplayDateFormat(date: Date) {
		const datePicker = this.datePickerRef?.nativeElement;

		if (datePicker && date !== null) {
			const formatted = moment(date).format(this.DATE_FORMAT.LICENSE_DATE_FORMAT);
			datePicker.value = formatted;
			this.cd.detectChanges();
		}
	}

	onDatePickerInit(datePickerRef?: ElementRef) {
		this.datePickerRef = datePickerRef;
	}

	onFocusOut(event: any) {
		this.candidateForm.markAsTouched();
		this.isOnline = navigator.onLine;
		clearOwnStaffNumberFailure(this.staffNumber);
		if (this.rejectOwnStaffNumber()) return;

		this.cvs.validateStaffId(this.staffNumber);
		if (this.staffNumber.valid && this.staffNumber.value.length === 7) {
			if (!navigator.onLine) return;

			this.loaderService.setLoading(true);
			this.httpService.getByStaffNumber(this.staffNumber.value).subscribe({
				next: this.onSuccessStaffId.bind(this),
				error: this.onErrorStaffId.bind(this),
			});
		}
	}

	/** Blocks the lookup when the instructor enters their own staff number. */
	private rejectOwnStaffNumber(): boolean {
		if (!isInstructorOwnStaffNumber(this.staffNumber.value, this.profileService.profile?.staff_number)) {
			return false;
		}

		clearCandidateStaffDerivedFields(this.candidateForm);
		applyOwnStaffNumberFailure(this.staffNumber);
		this.loaderService.setLoading(false);
		this.cd.detectChanges();
		return true;
	}

	onSuccessStaffId(resp: { data: StaffDetails }) {
		const staffDetail = resp.data;

		// gets rank from filtered options (respects form type restrictions)
		const rankOption = this.sharedService.getFilteredRankOptions(this.formType).find((v) => v.code == staffDetail.rank);

		// set empty if no rank found
		const rank = rankOption ?? "";

		const licenseTypeOption =
			this.sharedService.getLicenseTypeOptions(this.formType)?.find((v) => v.code === staffDetail.licenseType) ?? "";

		// console.log(resp);
		this.name.patchValue(staffDetail.name);

		if (isEmpty(this.rank.value)) {
			this.rank.patchValue(rank);
		}

		if (isEmpty(this.licenseNo.value)) {
			this.licenseNo.patchValue(staffDetail.licenseNumber);
		}

		const licenseTypeControl = this.candidateForm.get("licenseType");
		if (licenseTypeControl && isEmpty(licenseTypeControl.value)) {
			licenseTypeControl.patchValue(licenseTypeOption);
		}

		patchStaffLicenseExpiryDate(this.licenseExpiryDate, staffDetail.licenseExpiryDate);

		this.datePickerInput.markAllAsTouched();
		this.candidateForm.markAsTouched();
		this.candidateForm.updateValueAndValidity();
		this.loaderService.setLoading(false);
		this.cd.detectChanges();
	}

	onErrorStaffId(resp: any) {
		if (resp.includes("No profile found")) {
			this.name.patchValue("");
			this.rank.patchValue("");
			this.licenseNo.patchValue("");
			this.staffNumber?.setErrors({ invalidStaffId: true });
		}
	}

	get name() {
		return this.candidateForm.get("name")!;
	}
	get rank() {
		return this.candidateForm.get("rank")!;
	}
	get staffNumber() {
		return this.candidateForm.get("staffNumber")!;
	}
	get pilotProfile() {
		return this.candidateForm.get("pilotProfile")!;
	}
	get licenseNo() {
		return this.candidateForm.get("licenseNo")!;
	}
	get licenseExpiryDate() {
		return this.candidateForm.get("licenseExpiryDate")!;
	}

	get sessionsDetails() {
		return this.candidateForm.controls["sessionDetails"] as UntypedFormGroup;
	}

	getSessionControl(sessionCode: string): UntypedFormControl | null {
		const control = this.candidateForm.get("sessionDetails." + sessionCode);
		return control instanceof UntypedFormControl ? control : null;
	}

	saveName(value: string): void {
		this.name.patchValue(value);
	}

	saveRank(value: string): void {
		this.rank.patchValue(value);
	}

	saveStaffNumber(value: string): void {
		this.staffNumber.patchValue(value);
	}

	savePilotProfile(value: string): void {
		this.pilotProfile.patchValue(value);
	}

	saveLicenseNo(value: string): void {
		this.licenseNo.patchValue(value);
	}

	saveLicenseExpiryDate(value: string): void {
		this.licenseExpiryDate.patchValue(value);
	}

	handleDateChange(dateString: string): void {
		if (!isEmpty(dateString)) {
			// this.currentDate = new Date(dateString);
			this.saveLicenseExpiryDate(dateString);
		}
	}

	get filteredRankOptions() {
		return this.sharedService.getFilteredRankOptions(this.formType);
	}
}
